import React from 'react';

const POPULATION_OPTIONS = [
  { label: 'Adult', value: 'adult' },
  { label: 'Youth', value: 'youth' },
  { label: 'Both', value: 'both' },
];

const SERVICE_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Mental Health', value: 'mh' },
  { label: 'SUD', value: 'sud' },
  { label: 'Co-occurring', value: 'co_occurring' },
];

const SETTING_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Inpatient', value: 'inpatient' },
  { label: 'Outpatient', value: 'outpatient' },
  { label: 'Detox', value: 'detox' },
];

function SectionLabel({ children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
      {children}
    </p>
  );
}

function ToggleGroup({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${
              active
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const RADIUS_OPTIONS = [
  { label: '10 miles', value: '10' },
  { label: '25 miles', value: '25' },
  { label: '50 miles', value: '50' },
  { label: '100 miles', value: '100' },
];

export default function FilterPanel({ filters, onChange, counties = [] }) {
  const set = (key) => (value) => onChange({ ...filters, [key]: value });

  const zipActive = filters.zip && filters.zip.replace(/\D/g, '').length === 5;

  const handleZipChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    onChange({ ...filters, zip: val, county: val.length === 5 ? '' : filters.county });
  };

  return (
    <div className="space-y-6">
      {/* Distance from ZIP */}
      <div>
        <SectionLabel>Distance from ZIP</SectionLabel>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="ZIP code"
            value={filters.zip || ''}
            onChange={handleZipChange}
            className="w-28 flex-shrink-0 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <select
            value={filters.radius || '25'}
            onChange={(e) => set('radius')(e.target.value)}
            disabled={!zipActive}
            className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {RADIUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {zipActive && (
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1.5">County filter disabled while searching by ZIP</p>
        )}
      </div>

      {/* County */}
      <div>
        <SectionLabel>County</SectionLabel>
        <select
          value={filters.county}
          onChange={(e) => set('county')(e.target.value)}
          disabled={zipActive}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <option value="">All Counties</option>
          {counties.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* Population */}
      <div>
        <SectionLabel>Population</SectionLabel>
        <ToggleGroup
          options={POPULATION_OPTIONS}
          value={filters.population}
          onChange={set('population')}
        />
      </div>

      {/* Service Type */}
      <div>
        <SectionLabel>Service Type</SectionLabel>
        <ToggleGroup
          options={SERVICE_TYPE_OPTIONS}
          value={filters.service_type}
          onChange={set('service_type')}
        />
      </div>

      {/* Setting */}
      <div>
        <SectionLabel>Setting</SectionLabel>
        <ToggleGroup
          options={SETTING_OPTIONS}
          value={filters.setting}
          onChange={set('setting')}
        />
      </div>

      {/* Medication Services */}
      <div>
        <SectionLabel>Medication Services</SectionLabel>
        <div className="flex flex-col gap-2">
          {[
            { key: 'psych_meds', label: 'Psychiatric Medication Management' },
            { key: 'mat',        label: 'MAT / Opioid Treatment Program' },
            { key: 'ppw',        label: 'Pregnant / Parenting Women (PPW)' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!filters[key]}
                onChange={(e) => set(key)(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Availability */}
      <div>
        <SectionLabel>Availability</SectionLabel>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!filters.available_beds}
            onChange={(e) => set('available_beds')(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Has available beds (self-reported)</span>
        </label>
      </div>
    </div>
  );
}
